import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Send,
  Plus,
  Trash2,
  MessageCircle,
  Bot,
  User,
  Loader2,
  Paperclip,
  Camera,
  X,
  Image as ImageIcon,
  FileText,
  Download,
  Share2,
  Bookmark,
  BookmarkCheck,
  Volume2,
  Square,
  GraduationCap,
  Dumbbell,
  CheckCircle2,
  XCircle,
  Lightbulb,
  RefreshCw,
  Link2,
  Settings2
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import Cropper from "react-easy-crop";
import { RotateCcw, RotateCw, Crop as CropIcon } from "lucide-react";

// Build a cropped + rotated image blob from react-easy-crop pixel data
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (e) => reject(e));
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const rad = (rotation * Math.PI) / 180;
  const bBoxWidth = Math.abs(Math.cos(rad) * image.width) + Math.abs(Math.sin(rad) * image.height);
  const bBoxHeight = Math.abs(Math.sin(rad) * image.width) + Math.abs(Math.cos(rad) * image.height);
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);
  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(data, 0, 0);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve({ blob, dataUrl: canvas.toDataURL("image/jpeg", 0.92) });
    }, "image/jpeg", 0.92);
  });
}

// Markdown component overrides defined at module scope to avoid
// re-creating component types on every render (react/no-unstable-nested-components).
const markdownComponents = {
  h1: ({children}) => <h3 className="text-lg font-bold mt-5 mb-2 text-foreground">{children}</h3>,
  h2: ({children}) => <h3 className="text-base font-bold mt-5 mb-2 text-foreground">{children}</h3>,
  h3: ({children}) => <h4 className="text-sm font-bold mt-4 mb-1.5 text-foreground">{children}</h4>,
  p: ({children}) => <p className="mb-3 leading-7">{children}</p>,
  strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
  ul: ({children}) => <ul className="mb-3 pl-5 space-y-1.5 list-disc">{children}</ul>,
  ol: ({children}) => <ol className="mb-3 pl-5 space-y-1.5 list-decimal">{children}</ol>,
  li: ({children}) => <li className="leading-7">{children}</li>,
  hr: () => <hr className="my-4 border-border" />,
  blockquote: ({children}) => <blockquote className="border-l-3 border-primary/30 pl-4 my-3 text-muted-foreground italic">{children}</blockquote>,
  code: ({inline, children}) => inline
    ? <code className="bg-muted px-1.5 py-0.5 rounded text-[12px] md:text-[13px] font-mono text-foreground break-all">{children}</code>
    : <pre className="bg-muted rounded-lg p-3 md:p-4 my-3 overflow-x-auto"><code className="text-[12px] md:text-[13px] font-mono text-foreground">{children}</code></pre>,
  table: ({children}) => <div className="overflow-x-auto my-3 -mx-2"><table className="w-full border-collapse border border-border text-xs md:text-sm">{children}</table></div>,
  th: ({children}) => <th className="border border-border bg-muted px-2 md:px-3 py-1.5 md:py-2 text-left font-semibold">{children}</th>,
  td: ({children}) => <td className="border border-border px-2 md:px-3 py-1.5 md:py-2">{children}</td>,
};

export default function AIChatPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const messagesScrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [attachments, setAttachments] = useState([]); // [{id, file, preview}]

  // Image crop/rotate before sending
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropMeta, setCropMeta] = useState({ name: "photo.jpg", type: "image/jpeg" });
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropProcessing, setCropProcessing] = useState(false);
  const [cropAspect, setCropAspect] = useState(undefined); // undefined = freeform
  const [originalAspect, setOriginalAspect] = useState(null);
  const [bookmarks, setBookmarks] = useState([]); // [{bookmark_id, session_id, created_at, ...}]
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [bookmarkingIdx, setBookmarkingIdx] = useState(null);

  // Age adaptation
  const [grade, setGrade] = useState("");
  const [gradeOptions, setGradeOptions] = useState([]);

  // Voice answers (read-aloud)
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voiceRate, setVoiceRate] = useState(() => parseFloat(localStorage.getItem("mb_voice_rate")) || 0.95);
  const [voiceURI, setVoiceURI] = useState(() => localStorage.getItem("mb_voice_uri") || "");

  // Deep link sharing
  const [sharingLinkIdx, setSharingLinkIdx] = useState(null);

  // Practice generator
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceProblems, setPracticeProblems] = useState([]);
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [practiceResults, setPracticeResults] = useState({});
  const [practiceRevealed, setPracticeRevealed] = useState({});
  const [practiceSourceIdx, setPracticeSourceIdx] = useState(null);

  // Build a fast lookup key for "is this message bookmarked?"
  // Match strategy: prefer (session_id + created_at). Fallback to ai_response hash
  // for newly-sent messages whose persisted created_at isn't in client state yet.
  const isBookmarked = (msg) => bookmarks.some(
    b => (b.session_id === currentSessionId && b.created_at && msg.created_at && b.created_at === msg.created_at)
      || (b.ai_response && msg.ai_response && b.ai_response === msg.ai_response)
  );
  const getBookmarkId = (msg) => {
    const found = bookmarks.find(
      b => (b.session_id === currentSessionId && b.created_at && msg.created_at && b.created_at === msg.created_at)
        || (b.ai_response && msg.ai_response && b.ai_response === msg.ai_response)
    );
    return found?.bookmark_id;
  };

  useEffect(() => { fetchSessions(); fetchBookmarks(); fetchProfile(); }, []);
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Stop any narration when leaving the page
  useEffect(() => {
    return () => { try { window.speechSynthesis?.cancel(); } catch (e) { /* ignore */ } };
  }, []);

  // Load available speech voices (English preferred)
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const load = () => {
      const all = window.speechSynthesis.getVoices() || [];
      const eng = all.filter(v => (v.lang || "").toLowerCase().startsWith("en"));
      setVoices(eng.length ? eng : all);
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const updateRate = (vals) => {
    const r = Array.isArray(vals) ? vals[0] : vals;
    setVoiceRate(r);
    localStorage.setItem("mb_voice_rate", String(r));
  };
  const updateVoice = (uri) => {
    setVoiceURI(uri);
    localStorage.setItem("mb_voice_uri", uri);
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/ai-chat/profile`, { headers });
      setGrade(res.data.grade || "");
      setGradeOptions(res.data.grade_options || []);
    } catch (e) { /* ignore */ }
  };

  const saveGrade = async (value) => {
    setGrade(value);
    try {
      await axios.post(`${API}/ai-chat/profile`, { grade: value }, { headers });
      toast.success(`MathBot will now explain for ${value}`);
    } catch (e) {
      toast.error("Couldn't save your grade");
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/ai-chat/sessions`, { headers });
      setSessions(res.data);
    } catch (e) { /* ignore */ }
    setLoadingSessions(false);
  };

  const fetchBookmarks = async () => {
    try {
      const res = await axios.get(`${API}/ai-chat/bookmarks`, { headers });
      setBookmarks(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const toggleBookmark = async (msg, idx) => {
    if (!msg.ai_response) return;
    setBookmarkingIdx(idx);
    try {
      const existingId = getBookmarkId(msg);
      if (existingId) {
        await axios.delete(`${API}/ai-chat/bookmark/${existingId}`, { headers });
        setBookmarks(prev => prev.filter(b => b.bookmark_id !== existingId));
        toast.success("Bookmark removed");
      } else {
        const payload = {
          session_id: currentSessionId,
          user_message: msg.user_message || "",
          ai_response: msg.ai_response,
          created_at: msg.created_at || new Date().toISOString(),
          title: (msg.user_message || msg.ai_response).slice(0, 80)
        };
        const res = await axios.post(`${API}/ai-chat/bookmark`, payload, { headers });
        setBookmarks(prev => [
          {
            bookmark_id: res.data.bookmark_id,
            session_id: payload.session_id,
            user_message: payload.user_message,
            ai_response: payload.ai_response,
            created_at: payload.created_at,
            title: payload.title,
            bookmarked_at: new Date().toISOString()
          },
          ...prev
        ]);
        toast.success("Saved to your doubt vault");
      }
    } catch (e) {
      toast.error("Couldn't update bookmark. Try again.");
    } finally {
      setBookmarkingIdx(null);
    }
  };

  const removeBookmarkById = async (bookmarkId) => {
    try {
      await axios.delete(`${API}/ai-chat/bookmark/${bookmarkId}`, { headers });
      setBookmarks(prev => prev.filter(b => b.bookmark_id !== bookmarkId));
      toast.success("Bookmark removed");
    } catch (e) {
      toast.error("Couldn't remove bookmark");
    }
  };

  const loadSession = async (sessionId) => {
    setCurrentSessionId(sessionId);
    if (window.innerWidth < 768) setSidebarOpen(false);
    try {
      const res = await axios.get(`${API}/ai-chat/messages/${sessionId}`, { headers });
      setMessages(res.data);
    } catch (e) { toast.error("Failed to load conversation"); }
    clearAttachments();
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const startNewChat = () => {
    const newId = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setCurrentSessionId(newId);
    setMessages([]);
    clearAttachments();
    if (window.innerWidth < 768) setSidebarOpen(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await axios.delete(`${API}/ai-chat/sessions/${sessionId}`, { headers });
      setSessions(s => s.filter(x => x.session_id !== sessionId));
      if (currentSessionId === sessionId) { setCurrentSessionId(null); setMessages([]); }
      toast.success("Conversation deleted");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const MAX_ATTACHMENTS = 8;

  const addAttachmentItem = (file, preview) => {
    setAttachments(prev => {
      if (prev.length >= MAX_ATTACHMENTS) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files`);
        return prev;
      }
      return [...prev, { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, file, preview }];
    });
  };

  const addFilesDirect = (fileList) => {
    const items = [];
    fileList.forEach((file) => {
      const item = { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, file, preview: null };
      items.push(item);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (ev) =>
          setAttachments((p) => p.map((x) => (x.id === item.id ? { ...x, preview: ev.target.result } : x)));
        reader.readAsDataURL(file);
      }
    });
    setAttachments((prev) => {
      const combined = [...prev, ...items];
      if (combined.length > MAX_ATTACHMENTS) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files`);
        return combined.slice(0, MAX_ATTACHMENTS);
      }
      return combined;
    });
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    if (selected.some((f) => f.size > 10 * 1024 * 1024)) {
      toast.error("Each file must be under 10MB");
      e.target.value = "";
      return;
    }
    // Single image → open crop/rotate editor; multiple or non-image → add directly
    if (selected.length === 1 && selected[0].type.startsWith("image/")) {
      const file = selected[0];
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCropSrc(ev.target.result);
        setCropMeta({ name: file.name || "photo.jpg", type: "image/jpeg" });
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setCroppedAreaPixels(null);
        setCropAspect(undefined);
        setOriginalAspect(null);
        setCropOpen(true);
      };
      reader.readAsDataURL(file);
    } else {
      addFilesDirect(selected);
    }
    e.target.value = "";
  };

  const onCropComplete = (_area, areaPixels) => setCroppedAreaPixels(areaPixels);

  const applyCrop = async () => {
    if (!cropSrc || !croppedAreaPixels) { setCropOpen(false); return; }
    setCropProcessing(true);
    try {
      const { blob, dataUrl } = await getCroppedImg(cropSrc, croppedAreaPixels, rotation);
      const fname = (cropMeta.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
      addAttachmentItem(new File([blob], fname, { type: "image/jpeg" }), dataUrl);
      setCropOpen(false);
    } catch (err) {
      toast.error("Couldn't process the image. Sending the original.");
      const res = await fetch(cropSrc);
      const blob = await res.blob();
      addAttachmentItem(new File([blob], cropMeta.name || "photo.jpg", { type: blob.type || "image/jpeg" }), cropSrc);
      setCropOpen(false);
    } finally {
      setCropProcessing(false);
    }
  };

  const useOriginalImage = async () => {
    try {
      const res = await fetch(cropSrc);
      const blob = await res.blob();
      addAttachmentItem(new File([blob], cropMeta.name || "photo.jpg", { type: blob.type || "image/jpeg" }), cropSrc);
    } catch (e) { /* ignore */ }
    setCropOpen(false);
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((x) => x.id !== id));
  const clearAttachments = () => setAttachments([]);

  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || sending) return;
    const sessionId = currentSessionId || `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    if (!currentSessionId) setCurrentSessionId(sessionId);

    const userMsg = input.trim();
    const currentAttachments = attachments;

    setInput("");
    clearAttachments();
    if (inputRef.current) inputRef.current.style.height = "auto";

    // Build display message
    const displayMsg = {
      user_message: userMsg || (currentAttachments.length ? `[Attached: ${currentAttachments.length} file(s)]` : ""),
      ai_response: null,
      created_at: new Date().toISOString(),
      attachments: currentAttachments.map((a) => ({
        type: a.file.type.startsWith("image/") ? "image" : "file",
        filename: a.file.name,
        preview: a.preview
      }))
    };
    setMessages(prev => [...prev, displayMsg]);
    setSending(true);

    try {
      const formData = new FormData();
      formData.append("message", userMsg);
      formData.append("session_id", sessionId);
      currentAttachments.forEach((a) => formData.append("files", a.file));

      const res = await axios.post(`${API}/ai-chat/send`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        timeout: 60000
      });

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1].ai_response = res.data.response;
        return updated;
      });
      fetchSessions();
    } catch (error) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1].ai_response = "Sorry, I couldn't process that. Please try again.";
        return updated;
      });
      toast.error(error.response?.data?.detail || "Failed to get response");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const [savingPdf, setSavingPdf] = useState(null); // idx of msg being exported

  const exportSolutionAsPdf = async (msg, idx) => {
    setSavingPdf(idx);
    try {
      const node = document.getElementById(`solution-card-${idx}`);
      if (!node) {
        toast.error("Solution not found");
        setSavingPdf(null);
        return;
      }
      // html2canvas at 2x for crisp PDF
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Brand header on first page
      pdf.setFillColor(15, 27, 71);
      pdf.rect(0, 0, pageWidth, 18, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(13);
      pdf.setFont("helvetica", "bold");
      pdf.text("TheMatheMind 360 — AI Doubt Solver", margin, 11);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.text(new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }), pageWidth - margin, 11, { align: "right" });

      let y = 22;
      let remainingHeight = imgHeight;
      let sourceY = 0;
      const pageContentHeight = pageHeight - y - margin;

      if (imgHeight <= pageContentHeight) {
        pdf.addImage(imgData, "PNG", margin, y, imgWidth, imgHeight);
      } else {
        // Slice across pages
        while (remainingHeight > 0) {
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          const sliceHeight = Math.min(canvas.height - sourceY, (pageContentHeight * canvas.width) / imgWidth);
          sliceCanvas.height = sliceHeight;
          const ctx = sliceCanvas.getContext("2d");
          ctx.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
          const sliceImg = sliceCanvas.toDataURL("image/png");
          const sliceMmHeight = (sliceHeight * imgWidth) / canvas.width;
          pdf.addImage(sliceImg, "PNG", margin, y, imgWidth, sliceMmHeight);
          remainingHeight -= sliceMmHeight;
          sourceY += sliceHeight;
          if (remainingHeight > 0) {
            pdf.addPage();
            y = margin;
          }
        }
      }

      // Footer on last page
      const lastY = pdf.internal.pageSize.getHeight() - 6;
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text("Generated by MathBot · TheMatheMind 360 · Verify important calculations", pageWidth / 2, lastY, { align: "center" });

      const filename = `MathBot-Doubt-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
      toast.success("Solution saved as PDF");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save PDF. Try again.");
    } finally {
      setSavingPdf(null);
    }
  };

  const shareSolution = async (msg, idx) => {
    // Convert markdown + LaTeX to clean, readable plain text for email/WhatsApp
    const q = msg.user_message ? `Q: ${toPlainText(msg.user_message)}\n\n` : "";
    const text = `MathBot Solution\n${q}A:\n${toPlainText(msg.ai_response)}\n\n— Solved with MathBot (TheMatheMind 360)`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "MathBot Solution", text });
        return;
      } catch (e) {
        // user cancelled or share not supported; fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied! Paste it into WhatsApp/Email.");
    } catch (e) {
      toast.error("Couldn't copy. Try saving as PDF instead.");
    }
  };

  // ---- Convert markdown + LaTeX to clean readable plain text (for email/WhatsApp share) ----
  const toPlainText = (text) => {
    if (!text) return "";
    let t = text;
    // LaTeX constructs → readable symbols
    t = t.replace(/\\text\s*\{([^}]*)\}/g, "$1");
    t = t.replace(/\\mathrm\s*\{([^}]*)\}/g, "$1");
    t = t.replace(/\\frac\s*\{([^}]*)\}\s*\{([^}]*)\}/g, "($1)/($2)");
    t = t.replace(/\\sqrt\s*\{([^}]*)\}/g, "√($1)");
    t = t.replace(/\\times/g, "×").replace(/\\div/g, "÷").replace(/\\cdot/g, "·")
         .replace(/\\pm/g, "±").replace(/\\leq/g, "≤").replace(/\\geq/g, "≥")
         .replace(/\\neq/g, "≠").replace(/\\approx/g, "≈").replace(/\\degree/g, "°");
    t = t.replace(/\^\{([^}]*)\}/g, "^$1");
    t = t.replace(/_\{([^}]*)\}/g, "_$1");
    t = t.replace(/\\[a-zA-Z]+/g, "");        // strip remaining LaTeX commands
    t = t.replace(/\${1,2}/g, "");             // remove $ / $$ delimiters
    // Markdown → plain
    t = t.replace(/\*\*([^*]+)\*\*/g, "$1");   // bold
    t = t.replace(/\*/g, "");                   // stray emphasis asterisks
    t = t.replace(/^#{1,6}\s*/gm, "");          // headings
    t = t.replace(/^\s*[-*_]{3,}\s*$/gm, "");   // horizontal rules
    t = t.replace(/`([^`]*)`/g, "$1");          // inline code
    t = t.replace(/[{}\\]/g, "");               // stray braces/backslashes
    t = t.replace(/[ \t]{2,}/g, " ");
    t = t.replace(/\n{3,}/g, "\n\n");
    return t.trim();
  };

  // ---- Voice Answers: convert markdown/LaTeX to speakable plain text ----
  const toSpeech = (text) => {    if (!text) return "";
    let t = text;
    // Strip emojis / pictographs
    t = t.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u2705\u274C]/gu, " ");
    // LaTeX display/inline delimiters
    t = t.replace(/\$\$?/g, " ");
    // Common LaTeX commands to words
    t = t.replace(/\\times/g, " times ")
         .replace(/\\div/g, " divided by ")
         .replace(/\\pm/g, " plus or minus ")
         .replace(/\\cdot/g, " times ")
         .replace(/\\leq/g, " less than or equal to ")
         .replace(/\\geq/g, " greater than or equal to ")
         .replace(/\\sqrt\s*\{([^}]*)\}/g, " square root of $1 ")
         .replace(/\\frac\s*\{([^}]*)\}\s*\{([^}]*)\}/g, " $1 over $2 ")
         .replace(/\^\{?([0-9]+)\}?/g, " to the power $1 ")
         .replace(/\\[a-zA-Z]+/g, " ");
    // Markdown symbols
    t = t.replace(/[#*_`>~-]{1,}/g, " ")
         .replace(/\[(.*?)\]\(.*?\)/g, "$1");
    // Collapse whitespace
    t = t.replace(/\s+/g, " ").trim();
    return t;
  };

  const speakSolution = (msg, idx) => {
    if (!("speechSynthesis" in window)) {
      toast.error("Voice not supported on this device");
      return;
    }
    // Toggle: if already speaking this message, stop
    if (speakingIdx === idx) {
      window.speechSynthesis.cancel();
      setSpeakingIdx(null);
      return;
    }
    window.speechSynthesis.cancel();
    const speech = toSpeech(msg.ai_response);
    if (!speech) return;
    const utter = new SpeechSynthesisUtterance(speech);
    utter.rate = voiceRate;
    utter.pitch = 1.05;
    utter.lang = "en-IN";
    if (voiceURI) {
      const chosen = (window.speechSynthesis.getVoices() || []).find(v => v.voiceURI === voiceURI);
      if (chosen) utter.voice = chosen;
    }
    utter.onstart = () => setSpeakingIdx(idx);
    utter.onend = () => setSpeakingIdx(null);
    utter.onerror = () => setSpeakingIdx(null);
    setSpeakingIdx(idx);
    window.speechSynthesis.speak(utter);
  };

  // ---- Deep Link Sharing ----
  const shareSolutionLink = async (msg, idx) => {
    if (!msg.ai_response) return;
    setSharingLinkIdx(idx);
    try {
      const res = await axios.post(
        `${API}/ai-chat/share`,
        { ai_response: msg.ai_response, user_message: msg.user_message || "" },
        { headers }
      );
      const url = `${window.location.origin}/api/s/${res.data.share_id}`;
      if (navigator.share) {
        try {
          await navigator.share({ title: "MathBot Solution", url });
          return;
        } catch (e) { /* cancelled → fall through to clipboard */ }
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied! Anyone can open this solution.");
    } catch (e) {
      toast.error("Couldn't create a share link. Try again.");
    } finally {
      setSharingLinkIdx(null);
    }
  };

  // ---- Practice Generator ----
  const openPractice = async (msg, idx) => {
    setPracticeSourceIdx(idx);
    setPracticeOpen(true);
    setPracticeLoading(true);
    setPracticeProblems([]);
    setPracticeAnswers({});
    setPracticeResults({});
    setPracticeRevealed({});
    try {
      const res = await axios.post(`${API}/ai-chat/practice`, { ai_response: msg.ai_response }, { headers, timeout: 60000 });
      setPracticeProblems(res.data.problems || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate practice");
      setPracticeOpen(false);
    } finally {
      setPracticeLoading(false);
    }
  };

  const normalizeAns = (s) => (s || "").toString().toLowerCase().replace(/\s+/g, "").replace(/[,]/g, "");

  const checkPracticeAnswer = (i) => {
    const givenRaw = practiceAnswers[i];
    const given = normalizeAns(givenRaw);
    if (!given) { toast.error("Type your answer first"); return; }
    const correct = normalizeAns(practiceProblems[i]?.answer);
    let ok = given === correct;
    // Numeric equivalence (e.g. "12.0" == "12", "3/4" left as string)
    if (!ok) {
      const gn = parseFloat(given.replace(/[^0-9.\-]/g, ""));
      const cn = parseFloat(correct.replace(/[^0-9.\-]/g, ""));
      if (!Number.isNaN(gn) && !Number.isNaN(cn) && gn === cn && given.replace(/[0-9.\-]/g, "") === correct.replace(/[0-9.\-]/g, "")) {
        ok = true;
      }
    }
    setPracticeResults(prev => ({ ...prev, [i]: ok ? "correct" : "wrong" }));
  };

  return (
    <div className="h-[100dvh] flex bg-background overflow-hidden" data-testid="ai-chat-page">
      {/* Hidden file inputs */}
      <input ref={fileInputRef} type="file" multiple className="hidden" accept="image/*,.pdf,.txt,.csv" onChange={handleFileSelect} data-testid="file-input" />
      <input ref={cameraInputRef} type="file" multiple className="hidden" accept="image/*" capture="environment" onChange={handleFileSelect} data-testid="camera-input" />

      {/* Sidebar - Overlay on mobile, side panel on desktop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0 md:w-0"} fixed md:relative z-50 md:z-auto w-72 h-full transition-all duration-300 border-r border-border bg-background md:bg-muted/30 flex flex-col overflow-hidden`}>
        <div className="p-4 border-b border-border flex items-center justify-between gap-2">
          <h2 className="font-semibold text-foreground font-['Manrope'] text-sm shrink-0">Chats</h2>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setBookmarksOpen(true)}
              data-testid="open-bookmarks-btn"
              title="Doubt Vault — saved solutions"
              className="relative inline-flex items-center justify-center h-8 w-8 rounded-full border border-border hover:bg-muted transition-colors"
            >
              <Bookmark className="w-4 h-4" />
              {bookmarks.length > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-none">
                  {bookmarks.length > 99 ? "99+" : bookmarks.length}
                </span>
              )}
            </button>
            <Button size="sm" variant="outline" className="rounded-full h-8 px-3" onClick={startNewChat} data-testid="new-chat-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />New
            </Button>
            <button className="md:hidden p-1.5 rounded-lg hover:bg-muted" onClick={() => setSidebarOpen(false)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loadingSessions ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">No conversations yet</p>
          ) : (
            sessions.map((s) => (
              <div key={s.session_id}
                className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors text-sm ${currentSessionId === s.session_id ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"}`}
                onClick={() => loadSession(s.session_id)} data-testid={`session-${s.session_id}`}>
                <MessageCircle className="w-4 h-4 shrink-0" />
                <span className="flex-1 truncate">{s.last_message}</span>
                <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-red-500"
                  onClick={(e) => deleteSession(s.session_id, e)} data-testid={`delete-session-${s.session_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="h-14 border-b border-border flex items-center px-4 gap-3 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => navigate("/student")} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <MessageCircle className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-foreground font-['Manrope'] leading-tight">MathBot</h1>
              <p className="text-[11px] text-muted-foreground leading-tight truncate">Your AI Doubt Solver</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1.5 shrink-0">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-border hover:bg-muted transition-colors"
                  title="Voice settings"
                  data-testid="voice-settings-btn"
                >
                  <Settings2 className="w-4 h-4 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72" data-testid="voice-settings-panel">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-primary" />
                    <h4 className="text-sm font-semibold text-foreground">Narration Settings</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-muted-foreground">Speed</label>
                      <span className="text-xs font-medium text-foreground" data-testid="voice-rate-value">{voiceRate.toFixed(1)}x</span>
                    </div>
                    <Slider
                      min={0.5}
                      max={1.5}
                      step={0.1}
                      value={[voiceRate]}
                      onValueChange={updateRate}
                      data-testid="voice-rate-slider"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>Slower</span><span>Faster</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Voice</label>
                    <Select value={voiceURI || "default"} onValueChange={(v) => updateVoice(v === "default" ? "" : v)}>
                      <SelectTrigger className="h-9 text-xs" data-testid="voice-select">
                        <SelectValue placeholder="Default voice" />
                      </SelectTrigger>
                      <SelectContent data-testid="voice-options">
                        <SelectItem value="default" className="text-xs">Default voice</SelectItem>
                        {voices.map((v) => (
                          <SelectItem key={v.voiceURI} value={v.voiceURI} className="text-xs">
                            {v.name}{v.lang ? ` (${v.lang})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {voices.length === 0 && (
                      <p className="text-[10px] text-muted-foreground">No voices detected on this device.</p>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <GraduationCap className="w-4 h-4 text-muted-foreground hidden sm:block" />
            <Select value={grade} onValueChange={saveGrade}>
              <SelectTrigger className="h-8 w-[104px] sm:w-[130px] rounded-full text-[11px] px-3" data-testid="grade-select">
                <SelectValue placeholder="Set grade" />
              </SelectTrigger>
              <SelectContent data-testid="grade-options">
                {gradeOptions.map((g) => (
                  <SelectItem key={g} value={g} className="text-xs" data-testid={`grade-option-${g.replace(/\s+/g, '-')}`}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Messages */}
        <div ref={messagesScrollRef} className="flex-1 overflow-y-auto overscroll-contain px-3 md:px-4 py-4 md:py-6">
          {!currentSessionId && messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full max-w-lg mx-auto text-center px-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 md:mb-6">
                <Bot className="w-7 h-7 md:w-8 md:h-8 text-primary" />
              </div>
              <h2 className="text-lg md:text-xl font-bold font-['Manrope'] text-foreground mb-2">Hi {user?.name?.split(' ')[0]}! I&apos;m MathBot — your AI Doubt Solver</h2>
              <p className="text-xs md:text-sm text-muted-foreground mb-6 md:mb-8">Stuck on a math problem? Type your question or snap a photo — I&apos;ll solve it step by step!</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 w-full">
                {["How do I solve 45 + 78 on an abacus?", "What is 15% of 240?", "Explain fractions simply", "Help me with 234 × 56"].map((q, i) => (
                  <button key={i}
                    className="text-left text-xs md:text-sm px-3 md:px-4 py-2.5 md:py-3 rounded-xl border border-border hover:bg-muted/50 hover:border-primary/30 transition-all text-muted-foreground hover:text-foreground"
                    onClick={() => { startNewChat(); setInput(q); setTimeout(() => inputRef.current?.focus(), 100); }}
                    data-testid={`suggestion-${i}`}>{q}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((msg, idx) => (
                <div key={idx} className="space-y-4">
                  {/* User message */}
                  <div className="flex justify-end" data-testid={`user-msg-${idx}`}>
                    <div className="flex items-start gap-1.5 md:gap-2 max-w-[85%] md:max-w-[80%]">
                      <div className="space-y-2">
                        {(msg.attachments || (msg.attachment ? [msg.attachment] : [])).some(a => a?.type === "image" && a.preview) && (
                          <div className="flex flex-wrap gap-1.5 justify-end max-w-[300px] ml-auto">
                            {(msg.attachments || [msg.attachment]).filter(a => a?.type === "image" && a.preview).map((a, ai) => (
                              <div key={ai} className="rounded-xl overflow-hidden border border-border w-[92px] h-[92px]">
                                <img src={a.preview} alt="Attached" className="w-full h-full object-cover" />
                              </div>
                            ))}
                          </div>
                        )}
                        {(msg.attachments || (msg.attachment ? [msg.attachment] : [])).filter(a => a?.type === "file").map((a, ai) => (
                          <div key={ai} className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2 text-xs text-muted-foreground ml-auto w-fit">
                            <FileText className="w-4 h-4" />
                            {a.filename}
                          </div>
                        ))}
                        {msg.user_message && !msg.user_message.startsWith("[Attached:") && (
                          <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-4 py-2.5 text-sm whitespace-pre-wrap ml-auto max-w-[85%]">
                            {msg.user_message}
                          </div>
                        )}
                      </div>
                      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                  {/* AI response */}
                  <div className="flex justify-start" data-testid={`ai-msg-${idx}`}>
                    <div className="flex items-start gap-2 md:gap-3 max-w-[90%] md:max-w-[85%] w-full">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Bot className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div id={`solution-card-${idx}`} className="ai-response-content text-sm text-foreground leading-relaxed break-words overflow-hidden bg-white p-4 rounded-xl [&_.katex-display]:overflow-x-auto [&_.katex]:text-[0.85em] md:[&_.katex]:text-[0.95em]">
                          {msg.user_message && (
                            <div className="mb-3 pb-3 border-b border-border/60 text-xs text-muted-foreground">
                              <span className="font-semibold text-foreground">Q:</span> {msg.user_message}
                            </div>
                          )}
                          {msg.ai_response ? (
                            <ReactMarkdown
                              remarkPlugins={[remarkMath]}
                              rehypePlugins={[rehypeKatex]}
                              components={markdownComponents}
                            >
                              {msg.ai_response}
                            </ReactMarkdown>
                          ) : (
                            <div className="flex items-center gap-2 text-muted-foreground py-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Thinking...</span>
                            </div>
                          )}
                        </div>
                        {msg.ai_response && (
                          <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid={`solution-actions-${idx}`}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full h-7 text-[11px] px-2.5"
                              onClick={() => exportSolutionAsPdf(msg, idx)}
                              disabled={savingPdf === idx}
                              data-testid={`save-pdf-${idx}`}
                            >
                              {savingPdf === idx ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Saving...</>
                              ) : (
                                <><Download className="w-3 h-3 mr-1" />Save PDF</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full h-7 text-[11px] px-2.5"
                              onClick={() => shareSolution(msg, idx)}
                              data-testid={`share-${idx}`}
                            >
                              <Share2 className="w-3 h-3 mr-1" />
                              Share
                            </Button>
                            <Button
                              size="sm"
                              variant={isBookmarked(msg) ? "default" : "outline"}
                              className={`rounded-full h-7 text-[11px] px-2.5 ${isBookmarked(msg) ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                              onClick={() => toggleBookmark(msg, idx)}
                              disabled={bookmarkingIdx === idx}
                              data-testid={`bookmark-${idx}`}
                              title={isBookmarked(msg) ? "Remove bookmark" : "Bookmark this solution"}
                            >
                              {bookmarkingIdx === idx ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : isBookmarked(msg) ? (
                                <BookmarkCheck className="w-3 h-3 mr-1" />
                              ) : (
                                <Bookmark className="w-3 h-3 mr-1" />
                              )}
                              {isBookmarked(msg) ? "Bookmarked" : "Bookmark"}
                            </Button>
                            <Button
                              size="sm"
                              variant={speakingIdx === idx ? "default" : "outline"}
                              className={`rounded-full h-7 text-[11px] px-2.5 ${speakingIdx === idx ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                              onClick={() => speakSolution(msg, idx)}
                              data-testid={`listen-${idx}`}
                              title={speakingIdx === idx ? "Stop narration" : "Read this solution aloud"}
                            >
                              {speakingIdx === idx ? (
                                <><Square className="w-3 h-3 mr-1" />Stop</>
                              ) : (
                                <><Volume2 className="w-3 h-3 mr-1" />Listen</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full h-7 text-[11px] px-2.5"
                              onClick={() => openPractice(msg, idx)}
                              data-testid={`practice-${idx}`}
                              title="Turn this into a mini practice set"
                            >
                              <Dumbbell className="w-3 h-3 mr-1" />
                              Practice
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full h-7 text-[11px] px-2.5"
                              onClick={() => shareSolutionLink(msg, idx)}
                              disabled={sharingLinkIdx === idx}
                              data-testid={`share-link-${idx}`}
                              title="Copy a shareable link to this solution"
                            >
                              {sharingLinkIdx === idx ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Link…</>
                              ) : (
                                <><Link2 className="w-3 h-3 mr-1" />Copy link</>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Attachment Preview strip (multiple) */}
        {attachments.length > 0 && (
          <div className="border-t border-border px-4 py-2 bg-muted/20">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-muted-foreground" data-testid="attachment-count">
                  {attachments.length} file{attachments.length > 1 ? "s" : ""} attached
                </p>
                <button onClick={clearAttachments} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="clear-all-attachments-btn">
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="attachment-strip">
                {attachments.map((a) => (
                  <div key={a.id} className="relative group" data-testid={`attachment-item-${a.id}`}>
                    {a.preview ? (
                      <img src={a.preview} alt={a.file.name} className="h-16 w-16 object-cover rounded-lg border border-border" />
                    ) : (
                      <div className="h-16 w-16 rounded-lg border border-border bg-muted flex flex-col items-center justify-center px-1">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <span className="text-[9px] text-muted-foreground truncate w-full text-center mt-0.5">{a.file.name}</span>
                      </div>
                    )}
                    <button
                      onClick={() => removeAttachment(a.id)}
                      className="absolute -top-1.5 -right-1.5 bg-background border border-border rounded-full p-0.5 shadow-sm hover:bg-muted transition-colors"
                      data-testid={`remove-attachment-${a.id}`}
                    >
                      <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="border-t border-border p-2 md:p-4 shrink-0">
          <div className="max-w-3xl mx-auto flex items-end gap-1.5 md:gap-2">
            {/* Attachment buttons */}
            <div className="flex gap-0.5 shrink-0 pb-1">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 md:p-2.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Attach file or image"
                data-testid="attach-file-btn"
              >
                <Paperclip className="w-4 h-4 md:w-5 md:h-5" />
              </button>
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="p-2 md:p-2.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Take photo"
                data-testid="attach-camera-btn"
              >
                <Camera className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
            <textarea
              ref={inputRef}
              data-testid="chat-input"
              placeholder={attachments.length ? "Add a message..." : "Ask a math question..."}
              className="flex-1 resize-none rounded-xl border border-border bg-background px-3 md:px-4 py-2.5 md:py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary min-h-[44px] md:min-h-[48px] max-h-[200px] overflow-y-auto"
              rows={1}
              value={input}
              onChange={(e) => { setInput(e.target.value); requestAnimationFrame(() => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px"; }); }}
              onKeyDown={handleKeyDown}
            />
            <Button data-testid="send-btn" className="rounded-xl h-[44px] w-[44px] md:h-[48px] md:w-[48px] bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
              onClick={handleSend} disabled={(!input.trim() && attachments.length === 0) || sending}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] md:text-[11px] text-muted-foreground text-center mt-1.5 md:mt-2">MathBot can make mistakes. Always verify important calculations.</p>
        </div>
      </div>

      {/* Doubt Vault (Bookmarks) Dialog */}
      <Dialog open={bookmarksOpen} onOpenChange={setBookmarksOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="bookmarks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-primary" />
              Doubt Vault
              <span className="text-xs font-normal text-muted-foreground">({bookmarks.length})</span>
            </DialogTitle>
            <DialogDescription>
              Your saved AI solutions. Revisit any time — perfect for exam revision.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-3">
            {bookmarks.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground">
                <Bookmark className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No bookmarks yet. Tap the <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border text-xs"><Bookmark className="w-3 h-3" />Bookmark</span> button under any MathBot answer to save it here.
              </div>
            ) : (
              bookmarks.map((b) => (
                <div key={b.bookmark_id}
                  className="border border-border rounded-xl p-3 hover:border-primary/40 transition-colors"
                  data-testid={`bookmark-item-${b.bookmark_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">{b.title || "Saved solution"}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {b.bookmarked_at ? new Date(b.bookmarked_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => removeBookmarkById(b.bookmark_id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors shrink-0"
                      data-testid={`remove-bookmark-${b.bookmark_id}`}
                      title="Remove bookmark"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {b.user_message && (
                    <div className="mb-2 text-[11px] text-muted-foreground">
                      <span className="font-semibold text-foreground">Q:</span> {b.user_message.length > 140 ? b.user_message.slice(0, 140) + "…" : b.user_message}
                    </div>
                  )}
                  <details className="text-xs text-foreground">
                    <summary className="cursor-pointer text-primary hover:underline text-[11px] font-medium select-none">View full solution</summary>
                    <div className="mt-3 p-3 bg-muted/30 rounded-lg [&_.katex-display]:overflow-x-auto [&_.katex]:text-[0.85em]">
                      <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                        components={markdownComponents}
                      >
                        {b.ai_response}
                      </ReactMarkdown>
                    </div>
                  </details>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Practice Set Dialog */}
      <Dialog open={practiceOpen} onOpenChange={(o) => { setPracticeOpen(o); if (!o) { setPracticeProblems([]); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="practice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Dumbbell className="w-4 h-4 text-primary" />
              Practice Set
            </DialogTitle>
            <DialogDescription>
              Solve these problems to master the concept. Type your answer and tap Check.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-3">
            {practiceLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mb-3" />
                <p className="text-sm">Building your practice set…</p>
              </div>
            ) : practiceProblems.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground">No problems generated.</div>
            ) : (
              practiceProblems.map((p, i) => {
                const result = practiceResults[i];
                return (
                  <div key={i} className="border border-border rounded-xl p-3.5" data-testid={`practice-item-${i}`}>
                    <div className="flex items-start gap-2 mb-2.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                      <p className="text-sm text-foreground font-medium leading-6">{p.question}</p>
                    </div>
                    <div className="flex items-center gap-2 pl-7">
                      <Input
                        value={practiceAnswers[i] || ""}
                        onChange={(e) => setPracticeAnswers(prev => ({ ...prev, [i]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") checkPracticeAnswer(i); }}
                        placeholder="Your answer"
                        className={`h-9 text-sm max-w-[180px] ${result === "correct" ? "border-green-500" : result === "wrong" ? "border-red-500" : ""}`}
                        data-testid={`practice-input-${i}`}
                      />
                      <Button size="sm" variant="outline" className="h-9 rounded-lg text-xs" onClick={() => checkPracticeAnswer(i)} data-testid={`practice-check-${i}`}>
                        Check
                      </Button>
                      {result === "correct" && <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium" data-testid={`practice-correct-${i}`}><CheckCircle2 className="w-4 h-4" />Correct!</span>}
                      {result === "wrong" && <span className="inline-flex items-center gap-1 text-red-500 text-xs font-medium" data-testid={`practice-wrong-${i}`}><XCircle className="w-4 h-4" />Try again</span>}
                    </div>
                    <div className="pl-7 mt-2 flex items-center gap-3">
                      {p.hint && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setPracticeRevealed(prev => ({ ...prev, [i]: prev[i] === "hint" ? null : "hint" }))}
                          data-testid={`practice-hint-btn-${i}`}
                        >
                          <Lightbulb className="w-3.5 h-3.5" />{practiceRevealed[i] === "hint" ? "Hide hint" : "Hint"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setPracticeRevealed(prev => ({ ...prev, [i]: prev[i] === "answer" ? null : "answer" }))}
                        data-testid={`practice-reveal-btn-${i}`}
                      >
                        Show answer
                      </button>
                    </div>
                    {practiceRevealed[i] === "hint" && p.hint && (
                      <p className="pl-7 mt-1.5 text-[12px] text-muted-foreground italic" data-testid={`practice-hint-${i}`}>💡 {p.hint}</p>
                    )}
                    {practiceRevealed[i] === "answer" && (
                      <p className="pl-7 mt-1.5 text-[12px] text-foreground" data-testid={`practice-answer-${i}`}><span className="font-semibold">Answer:</span> {p.answer}</p>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {!practiceLoading && practiceProblems.length > 0 && practiceSourceIdx !== null && (
            <div className="pt-2 border-t border-border flex justify-end">
              <Button
                size="sm"
                variant="outline"
                className="rounded-full h-8 text-xs"
                onClick={() => openPractice(messages[practiceSourceIdx], practiceSourceIdx)}
                data-testid="practice-regenerate-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />New set
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Crop / Rotate editor */}
      <Dialog open={cropOpen} onOpenChange={(o) => { if (!o) setCropOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="crop-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CropIcon className="w-4 h-4 text-primary" />
              Crop & rotate your photo
            </DialogTitle>
            <DialogDescription>
              Trim to just the problem and straighten it so MathBot can read it clearly.
            </DialogDescription>
          </DialogHeader>
          <div className="relative w-full h-72 bg-black/90 rounded-xl overflow-hidden" data-testid="crop-area">
            {cropSrc && (
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={cropAspect}
                restrictPosition={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onMediaLoaded={(m) => setOriginalAspect(m.naturalWidth / m.naturalHeight)}
              />
            )}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground w-14">Aspect</span>
            <Button
              size="sm"
              variant={cropAspect === undefined ? "default" : "outline"}
              className="rounded-full h-8 text-xs px-3"
              onClick={() => setCropAspect(undefined)}
              data-testid="crop-aspect-freeform"
            >
              Freeform
            </Button>
            <Button
              size="sm"
              variant={cropAspect === 1 ? "default" : "outline"}
              className="rounded-full h-8 text-xs px-3"
              onClick={() => setCropAspect(1)}
              data-testid="crop-aspect-square"
            >
              Square
            </Button>
            <Button
              size="sm"
              variant={originalAspect && cropAspect === originalAspect ? "default" : "outline"}
              className="rounded-full h-8 text-xs px-3"
              onClick={() => originalAspect && setCropAspect(originalAspect)}
              disabled={!originalAspect}
              data-testid="crop-aspect-original"
            >
              Original
            </Button>
          </div>
          <div className="space-y-3 pt-1">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-14">Zoom</span>
              <Slider min={1} max={4} step={0.1} value={[zoom]} onValueChange={(v) => setZoom(v[0])} data-testid="crop-zoom-slider" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-14">Rotate</span>
              <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} data-testid="crop-rotate-left">
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="crop-rotate-right">
                <RotateCw className="w-4 h-4" />
              </Button>
              <span className="text-xs text-muted-foreground ml-1">{rotation}°</span>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" className="rounded-full" onClick={useOriginalImage} data-testid="crop-use-original">
              Use original
            </Button>
            <Button size="sm" className="rounded-full" onClick={applyCrop} disabled={cropProcessing} data-testid="crop-apply-btn">
              {cropProcessing ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Processing…</>) : (<>Use this crop</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
